import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useState, useRef, useMemo } from "preact/hooks"
import { addToCart, capitalize, getSrcset } from "../../helpers"
import { imageSwiperOptions } from "../../config"

export default function Product({ product, swatchData }) {
    const { format } = useCurrencyFormatting()

    const [ formOpen, setFormOpen ] = useState(false);
    const [ swatchProducts, setSwatchProducts ] = useState(null); // swatches grouped products
    const [ currentProduct, setCurrentProduct ] = useState(null); // clicked swatch
    const [ prices, setPrices ] = useState({ price: product.price, listPrice: product.listPrice });
    const [ sizes, setSizes ] = useState(product.skus.map(sku => (
        {
            id: sku.id,
            availability: sku.availability,
            size: sku.customFields.find(field => field.key === 'size')?.value,
        }
    )) || null);
    const swatchGroup = useMemo(() => product.customFields?.find(f => f.key === 'custom-associated_collection')?.value, []);
    const productColor = useMemo(() => product.skus?.[0]?.customFields?.find(field => field.key === 'color')?.value, []);
    const imageSliderRef = useRef(null);

    const productHandle = product.url?.split('/').pop()

    useEffect(() => {
        if(window.Swiper && product.alternateImageUrls?.length > 1 &&
            imageSliderRef.current && !imageSliderRef.current?.swiper
        ) {
            const options = { ...imageSwiperOptions };
            options.navigation = {
                nextEl: `.ns-product-${product.productId} .swiper-button-next`,
                prevEl: `.ns-product-${product.productId} .swiper-button-prev`,
            };
            options.breakpoints["1"].scrollbar = {
                el: `.ns-product-${product.productId} .swiper-scrollbar`,
                draggable: false,
            };

            const swiperInstance = new Swiper(imageSliderRef.current, options);
            imageSliderRef.current.swiper = swiperInstance;
            return () => {
                imageSliderRef.current.swiper.destroy(true, true);
                imageSliderRef.current.swiper = null;
            }
        }
    }, [])

    useEffect(() => {
        if(!swatchData || swatchProducts?.length) return;
        const swatches = swatchData.find(group => group.groupId === swatchGroup)?.products || null;
        //console.log("swatches: ", swatches)
        if(swatches) {
            setSwatchProducts(swatches);
        }
    }, [ swatchData, swatchProducts, swatchGroup ]);

    const handleColorChange = (event) => {
        const selectedId = event.target.value;
        if(selectedId === currentProduct?.id) return;
        const selectedProduct = swatchProducts.find(prod => prod.productId === selectedId);
        setCurrentProduct(selectedProduct);
        const selectedProductPrice = selectedProduct.price;
        const selectedProductListPrice = selectedProduct.listPrice;
        if(prices.price !== selectedProductPrice || prices.listPrice !== selectedProductListPrice) {
            setPrices({ price: selectedProductPrice, listPrice: selectedProductListPrice })
        }
        const selectedSizes = selectedProduct.skus?.map(sku => (
            {
                id: sku.id,
                size: sku.attributes?.find(attr => attr.key === 'Size')?.value,
                availability: sku.availability,
            }
        ));
        setSizes(selectedSizes);
    };

    const toggleForm = () => {
        if(window.innerWidth < 989) {
            setFormOpen(prevFormOpen => !prevFormOpen);
        }
    };

    const handleMouseEnter = () => {
        if(window.innerWidth >= 990 && !formOpen) {
            setFormOpen(true)
        }
    };

    const handleMouseLeave = () => {
        if(window.innerWidth >= 990 && formOpen) {
            setFormOpen(false)
        }
    };

    return (
        <SerpElement
            as="li"
            hit={product}
            className="ns-product"
            x-nosto-handle={productHandle}
        >
            <div
                className={`ns-product__wrap ns-product-${product.productId}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="ns-product__media">
                    <div className="ns-product__media-main">
                        <a href={product.url} aria-label={product.name} class="nosto-item-link">
                            <div className="ns-media ns-product__media-main-wrap">
                                <img
                                    class="ns-product__media-main-image ns-desktop-img"
                                    srcset={
                                        (currentProduct && currentProduct.imageUrl !== product.imageUrl)
                                            ? getSrcset(currentProduct.imageUrl)
                                            : getSrcset(product.imageUrl)
                                    }
                                    src={(currentProduct && currentProduct.imageUrl !== product.imageUrl)
                                        ? `${currentProduct.imageUrl}&width=940`
                                        : `${product.imageUrl}&width=940`
                                    }
                                    sizes="(max-width: 550px) 1px, (max-width: 989px) 360px, (min-width: 990px) 940px"
                                    alt={product.name}
                                    width={1440}
                                    height={1800}
                                    loading="eager"
                                />
                                <img
                                    class="ns-product__media-main-image ns-mobile-img"
                                    src={(currentProduct && currentProduct.imageUrl !== product.imageUrl)
                                        ? `${currentProduct.imageUrl}&width=360`
                                        : `${product.imageUrl}&width=360`
                                    }
                                    alt={product.name}
                                    loading="eager"
                                />
                            </div>
                        </a>
                    </div>

                    {product.alternateImageUrls?.length > 1 && (
                        <div class="ns-product__media_slider swiper" ref={imageSliderRef}>
                            <ul class="swiper-wrapper">
                                {product.alternateImageUrls.map((altImage, index) => (
                                    <li class="swiper-slide">
                                        <a href={product.url} aria-label={product.name} class="nosto-item-link">
                                            <div className="ns-media">
                                                <img
                                                    srcset={getSrcset(altImage)}
                                                    src={`${altImage}&width=533`}
                                                    sizes="(max-width: 550px) 1px, (max-width: 989px) 360px, (min-width: 990px) 940px"
                                                    className="ns-product__media_slider-image ns-desktop-img"
                                                    alt={product.name}
                                                />
                                                <img
                                                    src={`${altImage}&width=360`}
                                                    className="ns-product__media_slider-image ns-mobile-img"
                                                    alt={product.name}
                                                />
                                            </div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <div class="ns-product__media-slider-navs">
                                <button type="button" class="swiper-button swiper-button-prev">
                                    <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                                    </svg>
                                </button>
                                <button type="button" class="swiper-button swiper-button-next">
                                    <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                                    </svg>
                                </button>
                            </div>
                            <div class="swiper-scrollbar"></div>
                        </div>
                    )}
                </div>

                <div className="ns-product__meta">
                    <div className="ns-product__tags"></div>
                    <div className="ns-product__info">
                        <div className="ns-product__info-inner">
                            <div className="ns-product__details">
                                <h3 className="ns-product__prod-name">
                                    <a className="ns-product__prod-name-link nosto-item-link" href={product.url}>
                                        {product.name}
                                    </a>
                                </h3>
                                <div className="ns-product__price-wrap">
                                    {(prices.listPrice && prices.listPrice > prices.price) ? (
                                        <>
                                            <span className="visually-hidden">Regular Price</span>
                                            <span><s class="nosto-list-price">{format(prices.listPrice).toString().replace(".00", "")}</s></span>
                                            <span className="visually-hidden">Sale Price</span>
                                            <span class="nosto-price">{format(prices.price).toString().replace(".00", "")}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="visually-hidden">Regular Price</span>
                                            <span class="nosto-price">{format(prices.price).toString().replace(".00", "")}</span>
                                        </>
                                    )
                                }
                                </div>
                            </div>

                            <div className="ns-product__form">
                                <button
                                    type="button"
                                    className="ns-product__form-toggle"
                                    aria-expanded={formOpen}
                                    onClick={toggleForm}
                                    aria-label="Click to select product options"
                                >
                                    <span>+ Add</span>
                                </button>
                                {(swatchGroup && swatchProducts?.length > 0) &&
                                    <span className="ns-product__colors-tag">More Colors</span>
                                }

                                <div
                                    className="ns-product__form-details"
                                    aria-hidden={!formOpen}
                                >
                                    {(swatchGroup && swatchProducts?.length > 0) &&
                                        <fieldset className="ns-product__colors">
                                            {(currentProduct?.color || productColor) &&
                                                <legend className="visually-hidden">Color: {capitalize(currentProduct?.color || productColor)}</legend>
                                            }
                                            {swatchProducts.map((prod, index) => {
                                                // Build style string - prioritize image over color
                                                let swatchStyle = '';
                                                if (prod.swatchImage) {
                                                    // Use background-image if available
                                                    swatchStyle = `background-image: url(${prod.swatchImage}); background-size: cover; background-position: center;`;
                                                } else if (prod.hex) {
                                                    // Fallback to background-color
                                                    swatchStyle = `background-color: ${prod.hex}`;
                                                }

                                                // Check if this swatch should be checked
                                                // If currentProduct is set, match it; otherwise check the first swatch
                                                const isChecked = currentProduct
                                                    ? prod.productId === currentProduct.productId
                                                    : index === 0;

                                                return (
                                                    <label className="ns-product__color-label">
                                                        <input
                                                            type="radio"
                                                            className="visually-hidden"
                                                            value={prod.productId}
                                                            checked={isChecked}
                                                            onChange={handleColorChange}
                                                        />
                                                        <span
                                                            className="ns-product__color-swatch"
                                                            style={swatchStyle}
                                                        >
                                                            <span className="visually-hidden">{capitalize(prod.color)}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </fieldset>
                                    }
                                    {sizes?.length &&
                                        <div className="ns-product__sizes">
                                            <span className="visually-hidden">Product Size</span>
                                            {sizes.map(variant => (
                                                <button
                                                    type="button"
                                                    className="ns-product__sizes-atc"
                                                    disabled={variant.availability !== 'InStock'}
                                                    onClick={() => addToCart(variant.id)}
                                                >
                                                    {variant.size}
                                                    {(variant.availability !== 'InStock') &&
                                                        <span className="visually-hidden">Variant sold out or unavailable</span>
                                                    }
                                                </button>
                                            ))}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SerpElement>
    )
}